import { useState, useEffect, useRef, useCallback } from "react"
import axios from "axios"
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library"
import { Stack, TextField, Alert, CircularProgress, Box, Autocomplete } from "@mui/material"
import { getProductListOverview, getSelfCheckoutProducts } from "../products/products"

const __ = wp.i18n.__

const containerStyle = {
  marginTop: "0px",
  position: "relative",
  width: "75%",
  aspectRatio: "1 / 1",
  backgroundColor: "#000",
  borderRadius: "8px",
  overflow: "hidden",
  marginLeft: "auto",
  marginRight: "auto"
}

const viewFinderStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  border: "3px solid #4CAF50",
  boxSizing: "border-box",
  borderRadius: "8px",
  pointerEvents: "none"
}

function QrScanner({ updateScanResult }) {
  const currencySymbol = frontendLocalizer?.currencySymbol || frontendLocalizer?.currency || "CHF"

  const [scanActive, setScanActive] = useState(true)
  const [manualInput, setManualInput] = useState("")
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [products, setProducts] = useState([])
  const [productsLoading, setProductsLoading] = useState(true)
  const videoRef = useRef(null)
  const codeReaderRef = useRef(null)
  const scanningRef = useRef(false)

  // Define handleScanResult first, before it's used in effects or other handlers
  const handleScanResult = useCallback(
    decodedText => {
      // Stop scanning and clean up camera resources
      setScanActive(false)
      scanningRef.current = false

      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.reset()
        } catch (err) {
          console.warn("Error resetting camera:", err)
        }
      }

      // Call parent callback
      updateScanResult(decodedText)
    },
    [updateScanResult]
  )

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const scProds = await getSelfCheckoutProducts()
        const response = await getProductListOverview()

        if (response.products) {
          const prod = response.products
          const reArrangedData = []

          Object.keys(prod).forEach(function (key) {
            const product = prod[key]

            if (scProds.includes(product.id)) {
              product.label = product.name + " (" + product.sku + ") — " + currencySymbol + " " + parseFloat(product.price).toFixed(2) + (product.weight ? " / " + product.weight + " " + product.weight_unit : "")
              reArrangedData.push(product)
            }
          })

          setProducts(reArrangedData)
        }
      } catch (err) {
        console.error("Error loading products:", err)
      } finally {
        setProductsLoading(false)
      }
    }

    loadProducts()
  }, [])

  useEffect(() => {
    if (!scanActive || !videoRef.current) return

    const initScanner = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Initialize the code reader
        const codeReader = new BrowserMultiFormatReader()
        codeReaderRef.current = codeReader

        // Get available video devices - call on instance, not the class
        const videoDevices = await codeReader.listVideoInputDevices()

        if (videoDevices.length === 0) {
          setError(__("No camera found. Please check your device permissions.", "pingvin_checkout"))
          setIsLoading(false)
          return
        }

        // Select back camera (environment) if available, otherwise use first device
        const selectedDevice = videoDevices.find(device => device.label.toLowerCase().includes("back") || device.label.toLowerCase().includes("environment")) || videoDevices[0]

        // Start continuous decoding from the video device
        const decodeFromVideo = async () => {
          try {
            await codeReader.decodeFromVideoDevice(selectedDevice.deviceId, videoRef.current, (result, err) => {
              if (result) {
                handleScanResult(result.getText())
              } else if (err && !(err instanceof NotFoundException)) {
                console.warn("Scanning error:", err)
              }
            })
          } catch (err) {
            if (err.name === "NotAllowedError") {
              setError(__("Camera access denied. Please grant permissions and try again.", "pingvin_checkout"))
            } else if (err.name === "NotFoundError") {
              setError(__("No camera device found on this device.", "pingvin_checkout"))
            } else if (!(err instanceof NotFoundException)) {
              console.error("Scanner initialization error:", err)
              setError(__("Failed to initialize camera. Please try again.", "pingvin_checkout"))
            }
            setIsLoading(false)
          }
        }

        await decodeFromVideo()
        scanningRef.current = true
        setIsLoading(false)
      } catch (err) {
        console.error("Scanner setup error:", err)
        setError(__("Failed to initialize camera. Please try again.", "pingvin_checkout"))
        setIsLoading(false)
      }
    }

    initScanner()

    return () => {
      scanningRef.current = false
      if (codeReaderRef.current) {
        codeReaderRef.current.reset()
      }
    }
  }, [scanActive, handleScanResult])

  function handleManualInput(value) {
    if (!value || !value.trim()) return

    handleScanResult(value.trim())
  }

  const handleAutocompleteChange = useCallback(
    (event, value) => {
      if (value) {
        handleScanResult(value.sku)
      }
    },
    [handleScanResult]
  )

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <strong>{__("Scan QR code or Barcode", "pingvin_checkout")}</strong>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {scanActive && (
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
          <Box sx={containerStyle}>
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 10
                }}
              >
                <CircularProgress />
                <span style={{ color: "#fff", fontSize: "12px" }}>{__("Initializing camera...", "pingvin_checkout")}</span>
              </Box>
            )}
            <video
              ref={videoRef}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: isLoading ? "none" : "block"
              }}
            />
            <div style={viewFinderStyle} />
          </Box>
        </Stack>
      )}

      {!productsLoading && products.length > 0 ? <Autocomplete sx={{ width: "100%" }} options={products} getOptionLabel={option => option.label} isOptionEqualToValue={(option, value) => option.id === value.id} onChange={handleAutocompleteChange} disablePortal renderInput={params => <TextField {...params} label={__("Or search products", "pingvin_checkout")} className="autocompleteField" placeholder={__("Type to search...", "pingvin_checkout")} />} /> : <TextField autoFocus placeholder={__("Or enter manually", "pingvin_checkout")} type="text" variant="outlined" value={manualInput} onChange={e => setManualInput(e.target.value)} onBlur={() => handleManualInput(manualInput)} onKeyDown={e => e.key === "Enter" && handleManualInput(manualInput)} sx={{ width: "100%" }} />}
    </Stack>
  )
}

export default QrScanner
